import { Check, ChevronDown, Search } from "lucide-react";
import { useMemo, useState, type ReactNode } from "react";
import { useFormContext } from "react-hook-form";

import { ColocaInput } from "@/shared/components/forms/ColocaInput";
import { ColocaPopover } from "@/shared/components/overlays/ColocaPopover";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/shared/components/primitives/form";
import { ALL_PHONE_COUNTRIES, type PhoneCountry } from "@/shared/data/phone-countries";
import { countryMatchesQuery, localizedCountryName } from "@/shared/data/phone-country-list";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

interface ColocaCountryFieldProps {
  /** Field name as registered in the parent `useForm` schema. */
  name: string;
  label?: ReactNode;
  description?: ReactNode;
  placeholder?: string;
  disabled?: boolean;
  /** ISO 3166-1 alpha-2 codes to offer (e.g. `["MX", "CO"]`). When omitted, every country is shown. */
  countries?: readonly string[];
}

function FlagIcon({ iso2, size = 16 }: { iso2: string | null; size?: number }) {
  if (!iso2) {
    return (
      <span
        aria-hidden="true"
        className="inline-grid shrink-0 place-items-center rounded-[2px] bg-muted text-[10px] text-muted-foreground"
        style={{ width: size + 6, height: size }}
      >
        🌐
      </span>
    );
  }
  return (
    <img
      src={`https://flagcdn.com/${iso2.toLowerCase()}.svg`}
      alt=""
      aria-hidden="true"
      loading="lazy"
      className="inline-block shrink-0 rounded-[2px] object-cover"
      style={{ width: size + 6, height: size }}
    />
  );
}

/**
 * Form-bound country picker. Same popover-with-search visual as `AccountCountrySelect` (settings), but wired
 * to the nearest React Hook Form context. The form value is an ISO 3166-1 alpha-2 code (e.g. `"CO"`).
 */
export function ColocaCountryField({
  name,
  label,
  description,
  placeholder,
  disabled,
  countries,
}: ColocaCountryFieldProps) {
  const { control } = useFormContext();
  const { t, activeLanguage } = useTranslations("global");
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  // The offered subset, named in the active language and sorted by that name — the catalog stores English
  // names and is ordered by ISO code, which in Spanish reads as an unsorted list of foreign words. `selected`
  // still resolves against the full catalog below, so a value stored before the subset narrowed keeps
  // rendering its real name instead of falling back to the placeholder.
  const available = useMemo(() => {
    const collator = new Intl.Collator(activeLanguage, { sensitivity: "base" });
    const subset = countries
      ? ALL_PHONE_COUNTRIES.filter((c) => countries.includes(c.iso2))
      : ALL_PHONE_COUNTRIES;
    return subset
      .map((c) => ({ ...c, name: localizedCountryName(c.iso2, activeLanguage) }))
      .sort((a, b) => collator.compare(a.name, b.name));
  }, [countries, activeLanguage]);

  // `countryMatchesQuery` rather than a local `includes`: the names above are localized, so they carry the
  // locale's diacritics and a raw comparison never finds "panama" or "mexico". Shared with the phone field
  // and settings' own picker, which had the same bug in the same shape.
  const filtered = useMemo(
    () => (query.trim() ? available.filter((c) => countryMatchesQuery(c, query)) : available),
    [available, query],
  );

  return (
    <FormField
      control={control}
      name={name}
      render={({ field, fieldState }) => {
        const raw = (field.value as string) ?? "";
        const stored = raw ? (ALL_PHONE_COUNTRIES.find((c) => c.iso2 === raw.toUpperCase()) ?? null) : null;
        const selected: PhoneCountry | null = stored
          ? { ...stored, name: localizedCountryName(stored.iso2, activeLanguage) }
          : null;

        return (
          <FormItem>
            {label && <FormLabel>{label}</FormLabel>}
            <FormControl>
              <ColocaPopover
                open={open}
                onOpenChange={(next) => {
                  setOpen(next);
                  if (!next) {
                    setQuery("");
                    field.onBlur();
                  }
                }}
                side="bottom"
                align="start"
                contentClassName="w-[var(--anchor-width)] min-w-[280px] p-0"
                trigger={
                  <button
                    type="button"
                    disabled={disabled}
                    aria-expanded={open}
                    aria-invalid={!!fieldState.error || undefined}
                    className={cn(
                      "flex h-9 w-full items-center gap-2 rounded-md border bg-background px-3 py-1 text-sm text-foreground shadow-xs transition-[color,box-shadow] outline-none",
                      "focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50",
                      "aria-expanded:border-ring",
                      "disabled:cursor-not-allowed disabled:opacity-50",
                      fieldState.error ? "border-destructive ring-destructive/20" : "border-input",
                    )}
                  >
                    <FlagIcon iso2={selected?.iso2 ?? null} />
                    <span
                      className={cn(
                        "flex-1 truncate text-left",
                        selected ? "text-foreground" : "text-muted-foreground",
                      )}
                    >
                      {selected?.name ?? placeholder ?? "—"}
                    </span>
                    <ChevronDown size={14} className="shrink-0 text-muted-foreground" aria-hidden />
                  </button>
                }
              >
                <div className="flex flex-col">
                  <div className="relative border-b border-border p-2">
                    <Search
                      size={13}
                      className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground"
                      aria-hidden
                    />
                    <ColocaInput
                      autoFocus
                      value={query}
                      onValueChange={setQuery}
                      placeholder={t("phone_field_search_country_placeholder")}
                      className="h-8 pl-7 text-sm"
                    />
                  </div>
                  <ul role="listbox" className="max-h-[260px] overflow-y-auto overscroll-contain p-1">
                    {filtered.length === 0 ? (
                      <li className="px-3 py-6 text-center text-sm text-muted-foreground">
                        {t("not_found_message")}
                      </li>
                    ) : (
                      filtered.map((c) => {
                        const isSelected = selected?.iso2 === c.iso2;
                        return (
                          <li key={c.iso2}>
                            <button
                              type="button"
                              role="option"
                              aria-selected={isSelected}
                              onClick={() => {
                                field.onChange(c.iso2);
                                setOpen(false);
                                setQuery("");
                              }}
                              className={cn(
                                "flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm transition-colors",
                                isSelected
                                  ? "bg-accent text-accent-foreground"
                                  : "text-foreground hover:bg-foreground/5",
                              )}
                            >
                              <FlagIcon iso2={c.iso2} />
                              <span className="flex-1 truncate">{c.name}</span>
                              {isSelected && (
                                <Check size={14} className="shrink-0 text-primary" aria-hidden />
                              )}
                            </button>
                          </li>
                        );
                      })
                    )}
                  </ul>
                </div>
              </ColocaPopover>
            </FormControl>
            {description && <FormDescription>{description}</FormDescription>}
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
}
